import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useClerk, useUser } from '@clerk/react';
import { i18n } from '@this+that/app-config';

import { useCustomToast } from '../hooks/useCustomToast';
import { BootFallback } from '../Layout/AppShell';
import { useUpdateUserUtm } from '../services/user.service';
import {
  readAdAttribution,
  readAttributionCookie,
  readFirstTouchAttribution,
} from '../utils/analytics/attributionCookie';
import { updateUnsafeMetadata } from '../utils/clerkMetadata/updateUnsafeMetadata';
import { resolveSupportedLanguage } from '../utils/constants/languages';
import { setSentryUser } from '../utils/sentry';

const UserSetupContext = createContext<{ userReady: boolean }>({
  userReady: false,
});

export function UserSetupProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { successMessage } = useCustomToast();
  const { updateUserUtm } = useUpdateUserUtm();

  const [userReady, setUserReady] = useState(false);
  const [setupPerformed, setSetupPerformed] = useState(false);

  const setTimeZoneCampaignAndLang = useCallback(async () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const storedLanguage = user?.unsafeMetadata?.ht_language as
      | string
      | undefined;
    const navigatorLanguage = navigator.language || navigator?.languages[0];
    const language = resolveSupportedLanguage(
      storedLanguage || navigatorLanguage,
    );

    // Last non-direct: params on the current URL are the most recent touch and
    // win; otherwise fall back to the durable apex attribution cookie (an
    // earlier touch that survived across tabs/redirects/days). Taken as a set,
    // so a URL source never gets mixed with a cookie campaign.
    //
    // The marketing site appends utm params to every signup link it renders,
    // which is the only way medium and campaign reach us — the attribution
    // cookie stores sources alone.
    //
    // This read sessionStorage until 2026-07-31. Nothing had written those keys
    // since June 2025 (d1ba06ea96 moved the read here and dropped the write), so
    // the branch was dead: every signup fell through to the cookie and medium
    // and campaign were silently lost.
    const urlParams = new URLSearchParams(window.location.search);
    const urlSource = urlParams.get('utm_source');
    const cookieAttribution = urlSource ? null : readAttributionCookie();

    const utmSource = (urlSource ?? cookieAttribution?.source) || undefined;
    const utmMedium =
      (urlSource ? urlParams.get('utm_medium') : cookieAttribution?.medium) ||
      undefined;
    const utmCampaign =
      (urlSource
        ? urlParams.get('utm_campaign')
        : cookieAttribution?.campaign) || undefined;

    const firstTouch = readFirstTouchAttribution();
    const firstTouchUtmSource = firstTouch?.source || undefined;

    // Also set at account creation, which is the copy CAPI reads. Kept here so
    // a user who consents after signing up still gets the fields recorded.
    const ad = readAdAttribution();

    if (user) {
      await updateUnsafeMetadata(user, {
        ht_timezone: tz,
        ht_language: language,
        ht_utm_source: utmSource,
        ht_utm_medium: utmMedium,
        ht_utm_campaign: utmCampaign,
        ht_first_utm_source: firstTouchUtmSource,
        ht_fbclid: ad?.fbclid || undefined,
        ht_fbclid_at: ad?.fbclidAt || undefined,
        ht_fbp: ad?.fbp || undefined,
        ht_ads_consent: ad?.adsConsentGranted ? true : undefined,
      });
    }

    if (utmSource || utmMedium || utmCampaign || firstTouchUtmSource) {
      await updateUserUtm({
        utmSource,
        utmMedium,
        utmCampaign,
        firstTouchUtmSource,
      });
    }

    return language;
  }, [user, updateUserUtm]);

  const checkInitialSetup = useCallback(async () => {
    const initialSetupCompleted = Boolean(
      user?.unsafeMetadata?.ht_initialSetupComplete,
    );

    if (initialSetupCompleted) {
      await setTimeZoneCampaignAndLang();
    }

    return initialSetupCompleted;
  }, [user, setTimeZoneCampaignAndLang]);

  useEffect(() => {
    setSentryUser(isSignedIn ? user?.id : undefined);
  }, [isSignedIn, user?.id]);

  const setupUser = useCallback(async () => {
    if (setupPerformed || !user?.id) return;

    setSetupPerformed(true);

    const initialSetupCompleted = await checkInitialSetup();

    const lang = user?.unsafeMetadata?.ht_language as string | undefined;
    const tz = user?.unsafeMetadata?.ht_timezone;

    let effectiveLanguage = resolveSupportedLanguage(lang);

    if (!lang || !tz || !initialSetupCompleted) {
      effectiveLanguage = await setTimeZoneCampaignAndLang();
    }

    if (effectiveLanguage !== i18n.language) {
      i18n.changeLanguage(effectiveLanguage);
    }

    setUserReady(true);
  }, [setupPerformed, user, checkInitialSetup, setTimeZoneCampaignAndLang]);

  useEffect(() => {
    const handleUserValidation = async () => {
      const isValidUser =
        user && user.emailAddresses && user.emailAddresses.length > 0;

      if (isValidUser) {
        await setupUser();
      } else if (
        user &&
        (!user.emailAddresses || user.emailAddresses.length === 0)
      ) {
        await signOut();
        successMessage(
          'User successfully registered. Please log in again to access the platform.',
        );
        setUserReady(true);
      } else if (!isSignedIn) {
        setUserReady(true);
      }
    };

    handleUserValidation();
  }, [user?.id, isSignedIn, setupUser, signOut, successMessage]);

  return (
    <UserSetupContext.Provider value={{ userReady }}>
      {userReady ? children : <BootFallback />}
    </UserSetupContext.Provider>
  );
}

export const useUserSetup = () => useContext(UserSetupContext);
